<script lang="ts">
	import { page } from '$app/stores';
	import ScrollReveal from '$lib/components/ScrollReveal.svelte';
	import GlowCard from '$lib/components/GlowCard.svelte';
	import MagneticButton from '$lib/components/MagneticButton.svelte';
	import SensorNetwork from '$lib/components/SensorNetwork.svelte';
	import { t, type Language } from '$lib/i18n';
	import elWatchLogo from '$lib/assets/el-watch-logo.png';

	let lang = $derived($page.params.lang as Language);
	let tr = $derived(t(lang));

	// Shared icon paths
	const ICONS = {
		// Vibration - signal/wave emanating (represents mechanical oscillation)
		vibration:
			'M9.348 14.652a3.75 3.75 0 010-5.304m5.304 0a3.75 3.75 0 010 5.304m-7.425 2.121a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M12 12h.008v.008H12V12z',
		// Temperature - thermometer
		thermometer:
			'M12 9a1 1 0 00-1 1v7.5a2.5 2.5 0 105 0V10a1 1 0 00-1-1h-3zM10 4a2 2 0 012-2 2 2 0 012 2v3h-4V4z',
		// Pressure - gauge/dial
		gauge: 'M12 6v6l4 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
		// Humidity - water droplet
		droplet:
			'M12 21c-3.5 0-6.5-2.686-6.5-6 0-3.866 6.5-11 6.5-11s6.5 7.134 6.5 11c0 3.314-3 6-6.5 6z',
		// Metering - chart bars representing pulse/meter counts
		counter:
			'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
		// Electrical - lightning bolt
		bolt: 'M13 10V3L4 14h7v7l9-11h-7z',
		// Arrow - for CTA buttons
		arrow: 'M17 8l4 4m0 0l-4 4m4-4H3'
	} as const;

	const sensorTypes = [
		{
			titleKey: 'sensors_type_vibration',
			descKey: 'sensors_type_vibration_desc',
			icon: ICONS.vibration
		},
		{
			titleKey: 'sensors_type_temperature',
			descKey: 'sensors_type_temperature_desc',
			icon: ICONS.thermometer
		},
		{
			titleKey: 'sensors_type_pressure',
			descKey: 'sensors_type_pressure_desc',
			icon: ICONS.gauge
		},
		{
			titleKey: 'sensors_type_humidity',
			descKey: 'sensors_type_humidity_desc',
			icon: ICONS.droplet
		},
		{
			titleKey: 'sensors_type_metering',
			descKey: 'sensors_type_metering_desc',
			icon: ICONS.counter
		},
		{ titleKey: 'sensors_type_current', descKey: 'sensors_type_current_desc', icon: ICONS.bolt }
	];

	const steps = [
		{
			titleKey: 'sensors_how_step1_title',
			descKey: 'sensors_how_step1_desc'
		},
		{
			titleKey: 'sensors_how_step2_title',
			descKey: 'sensors_how_step2_desc'
		},
		{
			titleKey: 'sensors_how_step3_title',
			descKey: 'sensors_how_step3_desc'
		}
	];
</script>

<svelte:head>
	<title>{tr('sensors_page_title')}</title>
	<meta name="description" content={tr('sensors_page_desc')} />
</svelte:head>

<!-- Hero + Content wrapper -->
<div class="relative">
	<!-- Aurora effect -->
	<div class="aurora-container">
		<div class="aurora-layer aurora-1"></div>
		<div class="aurora-layer aurora-2"></div>
		<div class="aurora-layer aurora-3"></div>
	</div>

	<!-- Hero -->
	<section class="relative pt-32 pb-20">
		<!-- Sensor Network Visualization -->
		<div class="absolute inset-0 overflow-hidden">
			<SensorNetwork class="opacity-60" />
		</div>
		<div
			class="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-transparent to-transparent"
		></div>
		<div class="absolute inset-0 bg-grid opacity-20"></div>

		<div class="relative max-w-7xl mx-auto px-6">
			<ScrollReveal>
				<div class="max-w-4xl">
					<p class="text-ameta-green font-medium mb-4 tracking-wide">{tr('sensors_page_label')}</p>
					<h1 class="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
						{tr('sensors_page_heading')}
					</h1>
					<p class="text-xl md:text-2xl text-slate-400 leading-relaxed max-w-3xl mb-10">
						{tr('sensors_page_subtitle')}
					</p>
					<MagneticButton
						href="/{lang}/#contact"
						class="btn-primary text-lg inline-flex items-center gap-3"
					>
						<span>{tr('sensors_page_cta')}</span>
						<svg
							aria-hidden="true"
							class="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d={ICONS.arrow}
							/>
						</svg>
					</MagneticButton>
				</div>
			</ScrollReveal>
		</div>
	</section>

	<!-- Problem Statement -->
	<section class="py-20">
		<div class="max-w-7xl mx-auto px-6">
			<ScrollReveal>
				<div class="text-center max-w-3xl mx-auto mb-16">
					<h2 class="text-3xl md:text-4xl font-bold mb-6">{tr('sensors_problem_title')}</h2>
					<p class="text-lg text-slate-400">{tr('sensors_problem_desc')}</p>
				</div>
			</ScrollReveal>

			<div class="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
				<ScrollReveal>
					<GlowCard>
						<div class="p-8">
							<div
								class="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center text-red-400 mb-6"
							>
								<svg
									aria-hidden="true"
									class="w-6 h-6"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
									/>
								</svg>
							</div>
							<h3 class="text-xl font-bold mb-3 text-red-400">{tr('sensors_problem_reactive')}</h3>
							<p class="text-slate-400">{tr('sensors_problem_reactive_desc')}</p>
						</div>
					</GlowCard>
				</ScrollReveal>

				<ScrollReveal delay={100}>
					<GlowCard>
						<div class="p-8">
							<div
								class="w-12 h-12 rounded-xl bg-ameta-green/20 flex items-center justify-center text-ameta-green mb-6"
							>
								<svg
									aria-hidden="true"
									class="w-6 h-6"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</div>
							<h3 class="text-xl font-bold mb-3 text-ameta-green">
								{tr('sensors_problem_proactive')}
							</h3>
							<p class="text-slate-400">{tr('sensors_problem_proactive_desc')}</p>
						</div>
					</GlowCard>
				</ScrollReveal>
			</div>
		</div>
	</section>

	<!-- Solution: El-Watch + Ameta -->
	<section class="py-20 relative section-gradient">
		<div class="max-w-7xl mx-auto px-6">
			<ScrollReveal>
				<div class="text-center max-w-3xl mx-auto mb-16">
					<h2 class="text-3xl md:text-4xl font-bold mb-6">{tr('sensors_solution_title')}</h2>
					<p class="text-lg text-slate-400">{tr('sensors_solution_desc')}</p>
				</div>
			</ScrollReveal>

			<div class="grid md:grid-cols-3 gap-8">
				<ScrollReveal>
					<GlowCard>
						<div class="p-8 text-center">
							<div
								class="w-16 h-16 rounded-2xl bg-ameta-green/10 flex items-center justify-center text-ameta-green mx-auto mb-6"
							>
								<svg
									aria-hidden="true"
									class="w-8 h-8"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
									/>
								</svg>
							</div>
							<h3 class="text-xl font-bold mb-3">{tr('sensors_solution_sensors')}</h3>
							<p class="text-slate-400">{tr('sensors_solution_sensors_desc')}</p>
						</div>
					</GlowCard>
				</ScrollReveal>

				<ScrollReveal delay={100}>
					<GlowCard>
						<div class="p-8 text-center">
							<div
								class="w-16 h-16 rounded-2xl bg-ameta-green/10 flex items-center justify-center text-ameta-green mx-auto mb-6"
							>
								<svg
									aria-hidden="true"
									class="w-8 h-8"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
									/>
								</svg>
							</div>
							<h3 class="text-xl font-bold mb-3">{tr('sensors_solution_platform')}</h3>
							<p class="text-slate-400">{tr('sensors_solution_platform_desc')}</p>
						</div>
					</GlowCard>
				</ScrollReveal>

				<ScrollReveal delay={200}>
					<GlowCard>
						<div class="p-8 text-center">
							<div
								class="w-16 h-16 rounded-2xl bg-ameta-green/10 flex items-center justify-center text-ameta-green mx-auto mb-6"
							>
								<svg
									aria-hidden="true"
									class="w-8 h-8"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
									/>
								</svg>
							</div>
							<h3 class="text-xl font-bold mb-3">{tr('sensors_solution_alerts')}</h3>
							<p class="text-slate-400">{tr('sensors_solution_alerts_desc')}</p>
						</div>
					</GlowCard>
				</ScrollReveal>
			</div>
		</div>
	</section>

	<!-- How it works -->
	<section class="py-20">
		<div class="max-w-7xl mx-auto px-6">
			<ScrollReveal>
				<div class="text-center max-w-3xl mx-auto mb-16">
					<h2 class="text-3xl md:text-4xl font-bold mb-6">{tr('sensors_how_title')}</h2>
				</div>
			</ScrollReveal>

			<div class="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
				{#each steps as step, i}
					<ScrollReveal delay={i * 100}>
						<div class="text-center">
							<div
								class="w-12 h-12 rounded-full bg-ameta-green/20 text-ameta-green text-xl font-bold flex items-center justify-center mx-auto mb-6"
							>
								{i + 1}
							</div>
							<h3 class="text-xl font-bold mb-3">{tr(step.titleKey)}</h3>
							<p class="text-slate-400">{tr(step.descKey)}</p>
						</div>
					</ScrollReveal>
				{/each}
			</div>
		</div>
	</section>

	<!-- Sensor Types -->
	<section class="py-20 relative section-gradient">
		<div class="max-w-7xl mx-auto px-6">
			<ScrollReveal>
				<div class="text-center max-w-3xl mx-auto mb-16">
					<h2 class="text-3xl md:text-4xl font-bold mb-6">{tr('sensors_types_title')}</h2>
					<p class="text-lg text-slate-400">{tr('sensors_types_subtitle')}</p>
				</div>
			</ScrollReveal>

			<div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
				{#each sensorTypes as sensor, i}
					<ScrollReveal delay={i * 50}>
						<GlowCard>
							<div class="p-6">
								<div
									class="w-12 h-12 rounded-xl bg-ameta-green/10 flex items-center justify-center text-ameta-green mb-4"
								>
									<svg
										aria-hidden="true"
										class="w-6 h-6"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="1.5"
											d={sensor.icon}
										/>
									</svg>
								</div>
								<h3 class="text-lg font-bold mb-2">{tr(sensor.titleKey)}</h3>
								<p class="text-slate-400 text-sm">{tr(sensor.descKey)}</p>
							</div>
						</GlowCard>
					</ScrollReveal>
				{/each}
			</div>
		</div>
	</section>

	<!-- El-Watch Partnership -->
	<section class="py-20 relative section-gradient">
		<div class="max-w-7xl mx-auto px-6">
			<ScrollReveal>
				<div class="max-w-3xl mx-auto">
					<GlowCard>
						<div class="p-8 md:p-12">
							<div class="flex flex-col md:flex-row md:items-start gap-8">
								<div class="flex-shrink-0">
									<img src={elWatchLogo} alt="El-Watch logo" class="h-12 md:h-16 w-auto" />
								</div>
								<div>
									<h2 class="text-2xl md:text-3xl font-bold mb-4">{tr('sensors_elwatch_title')}</h2>
									<p class="text-lg text-slate-400 mb-6">{tr('sensors_elwatch_desc')}</p>
									<a
										href="https://el-watch.com"
										target="_blank"
										rel="noopener noreferrer"
										class="inline-flex items-center gap-2 text-ameta-green hover:text-ameta-green/80 transition-colors"
									>
										<span>{tr('sensors_elwatch_link')}</span>
										<svg
											aria-hidden="true"
											class="w-4 h-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
											/>
										</svg>
									</a>
								</div>
							</div>
						</div>
					</GlowCard>
				</div>
			</ScrollReveal>
		</div>
	</section>
</div>

<!-- CTA -->
<section class="py-24 relative section-gradient">
	<div class="max-w-4xl mx-auto px-6 text-center">
		<ScrollReveal>
			<h2 class="text-3xl md:text-4xl font-bold mb-6">
				{tr('sensors_cta_title')}
			</h2>
			<p class="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
				{tr('sensors_cta_desc')}
			</p>
			<MagneticButton
				href="/{lang}/#contact"
				class="btn-primary text-lg inline-flex items-center gap-3 glow-green"
			>
				<span>{tr('sensors_cta_button')}</span>
				<svg
					aria-hidden="true"
					class="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={ICONS.arrow} />
				</svg>
			</MagneticButton>
		</ScrollReveal>
	</div>
</section>

<style>
	.aurora-container {
		position: absolute;
		inset: 0;
		overflow: hidden;
		pointer-events: none;
		mask-image: linear-gradient(to bottom, black 0%, black 70%, transparent 100%);
		-webkit-mask-image: linear-gradient(to bottom, black 0%, black 70%, transparent 100%);
	}

	.aurora-layer {
		position: absolute;
		width: 200%;
		height: 100%;
		left: -50%;
		background: linear-gradient(
			180deg,
			transparent 0%,
			rgba(34, 197, 94, 0.06) 10%,
			rgba(59, 130, 246, 0.08) 30%,
			rgba(139, 92, 246, 0.06) 50%,
			rgba(34, 197, 94, 0.04) 70%,
			transparent 100%
		);
		filter: blur(80px);
		transform-origin: top center;
	}

	.aurora-1 {
		animation: aurora-wave 18s ease-in-out infinite;
		opacity: 0.35;
	}

	.aurora-2 {
		animation: aurora-wave 24s ease-in-out infinite reverse;
		opacity: 0.28;
		animation-delay: -10s;
	}

	.aurora-3 {
		animation: aurora-wave 21s ease-in-out infinite;
		opacity: 0.22;
		animation-delay: -6s;
		background: linear-gradient(
			180deg,
			transparent 0%,
			rgba(6, 182, 212, 0.08) 20%,
			rgba(34, 197, 94, 0.06) 40%,
			rgba(59, 130, 246, 0.04) 60%,
			transparent 100%
		);
	}

	@keyframes aurora-wave {
		0% {
			transform: translateX(-15%) skewX(-12deg) scaleY(1) rotate(-2deg);
		}
		20% {
			transform: translateX(10%) skewX(8deg) scaleY(1.2) rotate(1deg);
		}
		40% {
			transform: translateX(-5%) skewX(-15deg) scaleY(0.85) rotate(-1deg);
		}
		60% {
			transform: translateX(15%) skewX(12deg) scaleY(1.15) rotate(2deg);
		}
		80% {
			transform: translateX(-10%) skewX(-8deg) scaleY(0.95) rotate(-1.5deg);
		}
		100% {
			transform: translateX(-15%) skewX(-12deg) scaleY(1) rotate(-2deg);
		}
	}
</style>
